import { useEffect, useRef, useState } from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown } from "lucide-react";
import "./OperationalDirectory.css";

export type OperationalSelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

export type OperationalSelectProps = {
  ariaLabel: string;
  value: string;
  options: OperationalSelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  menuLayer?: "default" | "overlay" | "dialog";
};

const focusableSelector = [
  "a[href]",
  "button",
  "input",
  "select",
  "textarea",
  "audio[controls]",
  "video[controls]",
  "[contenteditable]",
  "[tabindex]",
].join(",");

function isTabbable(element: HTMLElement) {
  if (
    element.tabIndex < 0 ||
    element.matches(":disabled") ||
    element.getAttribute("aria-disabled") === "true" ||
    element.closest('[aria-hidden="true"], [hidden], [inert]') ||
    (element.tagName === "INPUT" &&
      element.getAttribute("type")?.toLowerCase() === "hidden")
  ) {
    return false;
  }

  for (
    let current: HTMLElement | null = element;
    current;
    current = current.parentElement
  ) {
    const style = window.getComputedStyle(current);
    if (
      style.display === "none" ||
      style.visibility === "hidden" ||
      style.visibility === "collapse"
    ) {
      return false;
    }
  }

  return true;
}

function findAdjacentTabbable(trigger: HTMLElement, backwards: boolean) {
  const focusableElements = Array.from(
    document.querySelectorAll<HTMLElement>(focusableSelector),
  )
    .map((element, documentIndex) => ({ element, documentIndex }))
    .filter(({ element }) => isTabbable(element))
    .sort((first, second) => {
      const firstTabIndex = first.element.tabIndex;
      const secondTabIndex = second.element.tabIndex;

      if (firstTabIndex === secondTabIndex) {
        return first.documentIndex - second.documentIndex;
      }
      if (firstTabIndex === 0) return 1;
      if (secondTabIndex === 0) return -1;
      return firstTabIndex - secondTabIndex;
    })
    .map(({ element }) => element);
  const triggerIndex = focusableElements.indexOf(trigger);

  if (triggerIndex < 0) return null;
  return focusableElements[triggerIndex + (backwards ? -1 : 1)] ?? null;
}

export function OperationalSelect({
  ariaLabel,
  value,
  options,
  onChange,
  placeholder = "Selecione",
  disabled = false,
  menuLayer = "default",
}: OperationalSelectProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const tabDirectionRef = useRef<"forward" | "backward" | null>(null);
  const selectedIndex = options.findIndex((option) => option.value === value);
  const internalValues = options.map(
    (_, index) => `operational-option-${index}`,
  );

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  useEffect(() => {
    if (!open) return;
    const closeOnTab = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Tab") return;

      tabDirectionRef.current = event.shiftKey ? "backward" : "forward";
      setOpen(false);
    };
    document.addEventListener("keydown", closeOnTab);
    return () => document.removeEventListener("keydown", closeOnTab);
  }, [open]);

  return (
    <div className="operational-select">
      <SelectPrimitive.Root
        value={selectedIndex >= 0 ? internalValues[selectedIndex] : ""}
        open={open}
        disabled={disabled}
        onOpenChange={(nextOpen) => {
          if (nextOpen) tabDirectionRef.current = null;
          setOpen(nextOpen);
        }}
        onValueChange={(nextValue) => {
          const nextOption = options[internalValues.indexOf(nextValue)];
          if (!disabled && nextOption) onChange(nextOption.value);
        }}
      >
        <SelectPrimitive.Trigger
          ref={triggerRef}
          aria-label={ariaLabel}
          className="operational-select__trigger"
        >
          <SelectPrimitive.Value placeholder={placeholder} />
          <SelectPrimitive.Icon asChild>
            <ChevronDown size={16} aria-hidden="true" />
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>

        <SelectPrimitive.Portal>
          <SelectPrimitive.Content
            aria-label={ariaLabel}
            data-layer={menuLayer === "default" ? undefined : menuLayer}
            position="popper"
            side="bottom"
            sideOffset={4}
            align="start"
            collisionPadding={8}
            className="operational-select__menu"
            onCloseAutoFocus={(event) => {
              const tabDirection = tabDirectionRef.current;
              if (!tabDirection) return;

              event.preventDefault();
              tabDirectionRef.current = null;
              const tabTarget = triggerRef.current
                ? findAdjacentTabbable(
                    triggerRef.current,
                    tabDirection === "backward",
                  )
                : null;
              tabTarget?.focus();
            }}
          >
            <SelectPrimitive.Viewport className="operational-select__viewport">
              {options.map((option, index) => (
                <SelectPrimitive.Item
                  key={`${option.value}-${index}`}
                  value={internalValues[index]}
                  disabled={option.disabled}
                  className="operational-select__option"
                >
                  <SelectPrimitive.ItemText>
                    {option.label}
                  </SelectPrimitive.ItemText>
                  <SelectPrimitive.ItemIndicator asChild>
                    <Check data-slot="check" size={16} aria-hidden="true" />
                  </SelectPrimitive.ItemIndicator>
                </SelectPrimitive.Item>
              ))}
            </SelectPrimitive.Viewport>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>
    </div>
  );
}
